import React, { useState, useEffect } from 'react';
import { Utensils, Star, MapPin, DollarSign, Clock, Users, Search, Download, Plus, Phone, MessageSquare, Filter, Globe, Calendar, CheckCircle, AlertTriangle } from 'lucide-react';

const RestaurantsManagement = () => {
  const [selectedDestination, setSelectedDestination] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedCuisine, setSelectedCuisine] = useState('all');
  const [selectedPrice, setSelectedPrice] = useState('all');
  const [selectedAvailability, setSelectedAvailability] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Statistiques principales
  const stats = {
    totalPartners: 487,
    michelinTables: 68,
    reservations2024: 2843,
    confirmationRate: 98,
    averageRating: 4.8,
    averageTicket: 185
  };

  // Destinations disponibles
  const destinations = [
    { id: 'all', name: 'Toutes', count: 487, flag: '🌍' },
    { id: 'st-barth', name: 'St-Barthélemy', count: 52, flag: '🏝️' },
    { id: 'st-tropez', name: 'St-Tropez', count: 78, flag: '🇫🇷' },
    { id: 'monaco', name: 'Monaco', count: 45, flag: '🇲🇨' },
    { id: 'mykonos', name: 'Mykonos', count: 63, flag: '🇬🇷' },
    { id: 'ibiza', name: 'Ibiza', count: 71, flag: '🇪🇸' },
    { id: 'sardaigne', name: 'Sardaigne', count: 38, flag: '🇮🇹' },
    { id: 'cannes', name: 'Cannes', count: 41, flag: '🎬' },
    { id: 'mustique', name: 'Mustique', count: 18, flag: '🌴' },
    { id: 'anguilla', name: 'Anguilla', count: 24, flag: '🏖️' },
    { id: 'turks', name: 'Turks & Caicos', count: 27, flag: '🦩' },
    { id: 'dubai', name: 'Dubai', count: 30, flag: '🌆' }
  ];

  // Restaurants partenaires
  const restaurants = [
    {
      id: 'toiny',
      name: 'Le Toiny Restaurant',
      type: 'Gastronomique Française',
      location: 'St-Barthélemy, Toiny',
      rating: 4.9,
      priceRange: '€€€€',
      maxTables: 12,
      serviceHours: '19h-23h',
      features: ['Vue mer', 'Chef étoilé', 'Cave exceptionnelle', 'Menu végétarien'],
      availability: 'available',
      special: 'michelin',
      exclusive: true
    },
    {
      id: 'club55',
      name: 'Club 55',
      type: 'Beach Club Méditerranéen',
      location: 'St-Tropez, Pampelonne',
      rating: 4.8,
      priceRange: '€€€',
      maxTables: 50,
      serviceHours: '12h-02h',
      features: ['Plage privée', 'DJ Live', 'Valet parking', 'Boutique'],
      availability: 'limited',
      special: 'beach-club',
      remainingTables: 2
    },
    {
      id: 'louis-xv',
      name: 'Le Louis XV - Alain Ducasse',
      type: 'Haute Gastronomie Française',
      location: 'Monaco, Hôtel de Paris',
      rating: 5.0,
      priceRange: '€€€€',
      maxTables: 25,
      serviceHours: '19h30-22h',
      features: ['3 étoiles Michelin', 'Cave 400K bouteilles', 'Menu dégustation'],
      availability: 'waiting',
      special: 'michelin',
      michelineStars: 3
    },
    {
      id: 'scorpios',
      name: 'Scorpios Mykonos',
      type: 'Mediterranean Fusion',
      location: 'Mykonos, Paraga Beach',
      rating: 4.7,
      priceRange: '€€€',
      maxTables: 80,
      serviceHours: '12h-03h',
      features: ['Sunset ritual', 'DJ sets', 'Organic menu', 'Beach beds'],
      availability: 'available',
      special: 'sunset'
    },
    {
      id: 'sublimotion',
      name: 'Sublimotion',
      type: 'Gastro-Expérience Immersive',
      location: 'Ibiza, Hard Rock Hotel',
      rating: 5.0,
      priceRange: '€€€€+',
      maxTables: 12,
      serviceHours: '20h30',
      features: ['Plus cher du monde', '20 plats', 'VR Experience'],
      availability: 'available',
      special: 'members',
      pricePerPerson: 1500
    },
    {
      id: 'atmosphere',
      name: 'At.mosphere',
      type: 'Fine Dining International',
      location: 'Dubai, Burj Khalifa 122F',
      rating: 4.8,
      priceRange: '€€€€',
      maxTables: 30,
      serviceHours: '12h-15h/18h-02h',
      features: ['442m altitude', 'Vue 360°', 'Lounge bar', 'Dress code'],
      availability: 'available',
      special: 'highest'
    }
  ];

  // Planning de septembre (exemple)
  const weekPlan = [
    { date: 8, reservations: 3 },
    { date: 9, reservations: 5 },
    { date: 10, reservations: 2 },
    { date: 11, reservations: 8 },
    { date: 12, reservations: 12 },
    { date: 13, reservations: 15 },
    { date: 14, reservations: 7 }
  ];

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'limited': return 'bg-yellow-100 text-yellow-800';
      case 'waiting': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getAvailabilityText = (availability, restaurant) => {
    switch (availability) {
      case 'available': return 'Tables disponibles';
      case 'limited': return `${restaurant.remainingTables} tables restantes`;
      case 'waiting': return 'Liste d\'attente';
      default: return 'Non disponible';
    }
  };

  const getSpecialBadge = (special, restaurant) => {
    switch (special) {
      case 'michelin':
        return (
          <div className="flex items-center space-x-1 bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
            <Star size={12} className="fill-current" />
            <span>{restaurant.michelineStars || 2}★ MICHELIN</span>
          </div>
        );
      case 'beach-club':
        return (
          <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
            TOP 10 BEACH CLUBS
          </div>
        );
      case 'members':
        return (
          <div className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-medium">
            MEMBERS ONLY
          </div>
        );
      case 'sunset':
        return (
          <div className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-medium">
            BEST SUNSET
          </div>
        );
      case 'highest':
        return (
          <div className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-medium">
            HIGHEST RESTAURANT
          </div>
        );
      default:
        return null;
    }
  };

  const getExclusiveBadge = (exclusive) => {
    if (!exclusive) return null;
    return (
      <div className="bg-yellow-500 text-white px-2 py-1 rounded text-xs font-medium">
        EXCLUSIF PRIVIA
      </div>
    );
  };

  const filteredRestaurants = restaurants.filter(restaurant => {
    const matchesSearch = restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         restaurant.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         restaurant.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDestination = selectedDestination === 'all' || restaurant.location.toLowerCase().includes(selectedDestination.replace('-', ' '));
    const matchesType = selectedType === 'all' || restaurant.type.toLowerCase().includes(selectedType);
    const matchesCuisine = selectedCuisine === 'all' || restaurant.type.toLowerCase().includes(selectedCuisine);
    const matchesPrice = selectedPrice === 'all' || restaurant.priceRange.includes(selectedPrice);
    const matchesAvailability = selectedAvailability === 'all' || restaurant.availability === selectedAvailability;
    
    return matchesSearch && matchesDestination && matchesType && matchesCuisine && matchesPrice && matchesAvailability;
  });

  const RestaurantCard = ({ restaurant }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            {getSpecialBadge(restaurant.special, restaurant)}
            {getExclusiveBadge(restaurant.exclusive)}
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAvailabilityColor(restaurant.availability)}`}>
              {getAvailabilityText(restaurant.availability, restaurant)}
            </span>
          </div>
          
          <h3 className="font-semibold text-gray-800 text-lg mb-1">{restaurant.name}</h3>
          <p className="text-sm text-gray-600 mb-2">{restaurant.type}</p>
          
          <div className="flex items-center space-x-1 text-sm text-gray-500 mb-3">
            <MapPin size={14} />
            <span>{restaurant.location}</span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm mb-4">
            <div className="flex items-center space-x-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={14}
                  className={i < Math.floor(restaurant.rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'}
                />
              ))}
              <span className="font-medium">{restaurant.rating}/5</span>
            </div>
            
            <div className="flex items-center space-x-1">
              <DollarSign size={14} className="text-green-600" />
              <span className="font-medium">{restaurant.priceRange}</span>
              {restaurant.pricePerPerson && (
                <span className="text-gray-500">€{restaurant.pricePerPerson}/pers</span>
              )}
            </div>
            
            <div className="flex items-center space-x-1">
              <Users size={14} className="text-blue-600" />
              <span>{restaurant.maxTables} {restaurant.pricePerPerson ? 'Places' : 'Tables'} max</span>
            </div>
            
            <div className="flex items-center space-x-1">
              <Clock size={14} className="text-purple-600" />
              <span>{restaurant.serviceHours}</span>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 mb-4">
            {restaurant.features.map((feature, index) => (
              <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                {feature}
              </span>
            ))}
          </div>
        </div>
      </div>
      
      <div className="flex items-center space-x-3">
        <button className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
          <CheckCircle size={16} />
          <span>Réserver</span>
        </button>
        <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
          <Phone size={16} />
          <span>Appeler</span>
        </button>
        <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
          <MessageSquare size={16} />
          <span>Détails</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="restaurants-management">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Restaurants & Tables Partenaires</h1>
            <p className="text-gray-600 mt-1">Accès exclusif aux meilleures tables pour vos clients VIP</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Filter size={18} />
              <span>Filtres Avancés</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <Download size={18} />
              <span>Export</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700">
              <Plus size={18} />
              <span>Nouveau Partenaire</span>
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mt-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Utensils className="text-blue-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.totalPartners}</div>
            <div className="text-sm text-gray-600">Restaurants Partenaires</div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Star className="text-yellow-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.michelinTables}</div>
            <div className="text-sm text-gray-600">Tables Michelin</div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="text-green-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.reservations2024.toLocaleString()}</div>
            <div className="text-sm text-gray-600">Réservations 2024</div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="text-purple-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.confirmationRate}%</div>
            <div className="text-sm text-gray-600">Taux Confirmation</div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Star className="text-orange-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">{stats.averageRating}/5</div>
            <div className="text-sm text-gray-600">Note Moyenne</div>
          </div>

          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="text-teal-600" size={24} />
            </div>
            <div className="text-2xl font-bold text-gray-800">€{stats.averageTicket}</div>
            <div className="text-sm text-gray-600">Ticket Moyen</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Filtres destinations */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {destinations.map((destination) => (
              <button
                key={destination.id}
                onClick={() => setSelectedDestination(destination.id)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                  selectedDestination === destination.id
                    ? 'bg-yellow-50 border-yellow-300 text-yellow-800'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span>{destination.flag}</span>
                <span>{destination.name}</span>
                <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full">
                  {destination.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Filtres détaillés */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous types</option>
              <option value="gastronomique">Gastronomique</option>
              <option value="beach">Beach Club</option>
              <option value="traditionnel">Traditionnel</option>
              <option value="rooftop">Rooftop</option>
              <option value="lounge">Lounge Bar</option>
              <option value="yacht">Yacht Club</option>
            </select>

            <select
              value={selectedCuisine}
              onChange={(e) => setSelectedCuisine(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Toutes cuisines</option>
              <option value="francaise">Française</option>
              <option value="italienne">Italienne</option>
              <option value="japonaise">Japonaise</option>
              <option value="mediterraneenne">Méditerranéenne</option>
              <option value="fusion">Fusion</option>
              <option value="seafood">Seafood</option>
            </select>

            <select
              value={selectedPrice}
              onChange={(e) => setSelectedPrice(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Tous prix</option>
              <option value="€€€€">€€€€ (250€+)</option>
              <option value="€€€">€€€ (150-250€)</option>
              <option value="€€">€€ (75-150€)</option>
              <option value="€">€ (&lt; 75€)</option>
            </select>

            <select
              value={selectedAvailability}
              onChange={(e) => setSelectedAvailability(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              <option value="all">Toutes</option>
              <option value="available">Disponible ce soir</option>
              <option value="limited">Cette semaine</option>
              <option value="waiting">Week-end</option>
            </select>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Rechercher"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>

            <button className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Réinitialiser
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Liste des restaurants */}
          <div className="lg:col-span-2">
            <div className="space-y-6">
              {filteredRestaurants.map((restaurant) => (
                <RestaurantCard key={restaurant.id} restaurant={restaurant} />
              ))}
            </div>
          </div>

          {/* Sidebar planning */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                  <Calendar className="text-blue-600" size={20} />
                  <span>Planning des Réservations</span>
                </h3>
                <button className="text-sm text-blue-600 hover:text-blue-700">
                  Voir tout le planning
                </button>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700 mb-3">Septembre 2025</h4>
                {weekPlan.map((day) => (
                  <div key={day.date} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-yellow-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {day.date}
                      </div>
                      <span className="text-sm text-gray-700">
                        {day.reservations} réservations
                      </span>
                    </div>
                    <div className="w-16 h-2 bg-gray-200 rounded-full">
                      <div 
                        className="h-2 bg-yellow-500 rounded-full"
                        style={{ width: `${Math.min(day.reservations * 6.67, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantsManagement;